import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, firstValueFrom } from 'rxjs';
import { BffService } from 'src/app/shared/services/bff.service';
import { LogRocketService } from 'src/app/shared/services/logrocket.service';
import { VideoDataService } from 'src/app/shared/services/video-data.service';
import { ErrorDialogComponent } from '../../landing/components/error-dialog/error-dialog.component';
import { CredentialsStepComponent } from '../../login/components/credentials-step/credentials-step.component';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  standalone: true,
  imports: [CommonModule, CredentialsStepComponent],
})
export class LoginPageComponent implements OnInit {
  @ViewChild(CredentialsStepComponent) credentialsComponent?: CredentialsStepComponent;

  private readonly bffService = inject(BffService);
  private readonly logRocketService = inject(LogRocketService);
  private readonly videoDataService = inject(VideoDataService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  readonly isLoading = signal(false);
  readonly loginAttempts = signal(0);

  private userRut = '';
  private attemptsLimit = 3;

  id: string | null = null;

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.id = params['id'] || null;
    });
  }

  async onFormSubmit(credentials: { rut: string }): Promise<void> {
    if (!this.id) {
      console.error('ID query parameter is required');
      return;
    }

    this.isLoading.set(true);

    try {
      await this.login(credentials.rut, this.id);
    } catch (error: any) {
      this.handleLoginError(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async login(rut: string, id: string): Promise<void> {
    this.userRut = rut;

    await firstValueFrom(
      this.bffService.validateUuid(rut, id).pipe(
        catchError((error) => {
          console.error('Error validating UUID:', error);
          throw error;
        }),
      ),
    );

    this.logRocketService.init();
    this.logRocketService.identify(rut);

    const videoProperties = await firstValueFrom(
      this.bffService.getVideoProperties(rut, id).pipe(
        catchError((error) => {
          console.error('Error fetching video properties:', error);
          throw error;
        }),
      ),
    );

    this.videoDataService.setVideoData(videoProperties);
    this.videoDataService.setRut(this.userRut);
    await this.router.navigate(['/landing'], { queryParams: { id } });
  }

  private handleLoginError(error: unknown): void {
    console.log(error);

    if (!(error instanceof HttpErrorResponse)) {
      this.openErrorDialog('Ocurrió un error inesperado. Por favor intenta nuevamente.');
      return;
    }

    if (error.status === 403) {
      this.openErrorDialog('El RUT ingresado no coincide con este vídeo.');
      return;
    }

    if (error.status === 404) {
      this.handleVideoNotFound();
      return;
    }

    const message = error.error?.message || error.error || 'Ocurrió un error inesperado. Por favor intenta nuevamente.';
    this.openErrorDialog(message);
  }

  private handleVideoNotFound(): void {
    const attempts = this.loginAttempts() + 1;
    this.loginAttempts.set(attempts);

    if (attempts >= this.attemptsLimit) {
      this.loginAttempts.set(0);
      this.attemptsLimit = 1;
      this.credentialsComponent?.startCooldown();
      return;
    }

    const left = this.attemptsLimit - attempts;
    const message = `No se encontró vídeo para el RUT ingresado. Te queda${left !== 1 ? 'n' : ''} ${left} intento${left !== 1 ? 's' : ''}.`;
    this.credentialsComponent?.displayLoginError(message, left);
  }

  private openErrorDialog(message: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '560px',
      data: {
        title: 'Error',
        message,
      },
    });
  }
}
